var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { setElementOffset } from '../lib-domMutation';
import { getElementPositionRectangle } from '../lib-domUtils';
/**
 * Place element near the handle at some visible position according
 * to the order of positions defined in positions.
 *
 * @param positions Array of suitable positions sorted by descending priority.
 *                  If no positions are suitable, the first one will be applied.
 *                  i.e. [PlacementPosition.TOP, PlacementPosition.LEFT] at first will try to place an element
 *                  to the top of the handle, then to the left of the handle.
 * @param onBeforePlacement Called when new position and offsets were calculated and are about to set on element.
 * @param hasText used to ensure if tooltip target text takes more than one line on the page
 */
export function smartPlacement(handle, element, offsets, positions, onBeforePlacement, hasText) {
    if (positions === void 0) { positions = ['bottom-right', 'bottom-left', 'bottom', 'top-right', 'top-left', 'top', 'left', 'right']; }
    if (onBeforePlacement === void 0) { onBeforePlacement = function (newPosition, coordinates) { return coordinates; }; }
    if (hasText === void 0) { hasText = false; }
    var position = getVisiblePosition(handle, element, offsets, positions, hasText);
    var coordinates = getPlacementCoordinates(handle, element, position, offsets, hasText);
    coordinates = onBeforePlacement(position, coordinates);
    setElementOffset(element, coordinates);
    return position;
}
function unsetElementDimensions(element) {
    var prevStyles = {};
    ['top', 'left', 'width', 'height'].forEach(function (item) {
        prevStyles[item] = element.style[item];
        switch (item) {
            case 'top':
            case 'left':
                element.style[item] = '0px';
                break;
            case 'width':
            case 'height':
                element.style[item] = '';
                break;
        }
    });
    return function () {
        for (var prop in prevStyles) {
            element.style[prop] = prevStyles[prop];
        }
    };
}
/**
 * Calculate position based on visibility in viewport and priority of placement
 * @return Position of popover
 */
function getVisiblePosition(handle, element, offsets, positions, hasText) {
    if (hasText === void 0) { hasText = false; }
    var calculateVisibilityForPositions = function (position) {
        var coordinatesToTry = getPlacementCoordinates(handle, element, position, offsets);
        var _isOverflowingParent = getComputedStyle(element).position === 'fixed'
            ? false
            : isOverflowingParent(element, coordinatesToTry);
        return {
            position: position,
            isOverflowingViewport: isOverflowingViewport(element, coordinatesToTry),
            isOverflowingParent: _isOverflowingParent
        };
    };
    var isOverflowingViewport = function (element, coordinates) {
        var top = coordinates.top - window.pageYOffset;
        var left = coordinates.left - window.pageXOffset;
        var boundingClientRect = element.getBoundingClientRect();
        var elementRect = {
            top: top,
            left: left,
            bottom: top + boundingClientRect.height,
            right: left + boundingClientRect.width
        };
        var clientWidth = document.documentElement.clientWidth;
        var clientHeight = document.documentElement.clientHeight;
        return (elementRect.left < 0 ||
            elementRect.top < 0 ||
            elementRect.right > clientWidth ||
            elementRect.bottom > clientHeight);
    };
    var isOverflowingParent = function (element, coordinates) {
        var parentEl = element.parentElement;
        var boundingClientRect = element.getBoundingClientRect();
        var elementRect = __assign(__assign({}, coordinates), { bottom: coordinates.top + boundingClientRect.height, right: coordinates.left + boundingClientRect.width });
        // absolutely positioned element cause overflow for the first non-static positioned element
        while (parentEl && getComputedStyle(parentEl).position === 'static')
            parentEl = parentEl.parentElement;
        while (parentEl) {
            if (getComputedStyle(parentEl).position !== 'static') {
                var _a = getComputedStyle(parentEl), overflowX = _a.overflowX, overflowY = _a.overflowY;
                var hasOverflowY = /auto|scroll|overlay|hidden|clip/.test(overflowY);
                var hasOverflowX = /auto|scroll|overlay|hidden|clip/.test(overflowX);
                if (hasOverflowY && hasOverflowX) {
                    var parentRect = parentEl.getBoundingClientRect();
                    var round10 = function (value) { return +value.toFixed(1); };
                    var hasOverflowBottom = round10(elementRect.bottom) > round10(parentRect.bottom);
                    var hasOverflowTop = round10(elementRect.top) < round10(parentRect.top);
                    var hasOverflowRight = round10(elementRect.right) > round10(parentRect.right);
                    var hasOverflowLeft = round10(elementRect.left) < round10(parentRect.left);
                    return hasOverflowBottom || hasOverflowTop || hasOverflowRight || hasOverflowLeft;
                }
                // in case when one of overflowX or overflowY is 'clip' other can be 'visible'
                if (hasOverflowX) {
                    var parentRect = parentEl.getBoundingClientRect();
                    var isElementOutOfBounds = (elementRect.right > parentRect.right ||
                        elementRect.left < parentRect.left);
                    if (isElementOutOfBounds)
                        return true;
                }
                if (hasOverflowY) {
                    var parentRect = parentEl.getBoundingClientRect();
                    var isElementOutOfBounds = (elementRect.bottom > parentRect.bottom ||
                        elementRect.top < parentRect.top);
                    if (isElementOutOfBounds)
                        return true;
                }
            }
            parentEl = parentEl.parentElement;
        }
        return false;
    };
    var visibleInViewport = function (ratio) {
        return !ratio.isOverflowingViewport && !ratio.isOverflowingParent;
    };
    var setOriginalDimensions = unsetElementDimensions(element);
    var visiblePositions = positions
        .map(calculateVisibilityForPositions)
        .filter(visibleInViewport);
    setOriginalDimensions();
    var checkLines = multiLineTextPositionAndCoordinates(handle, element);
    if (hasText && checkLines.position) {
        return checkLines.position;
    }
    else if (visiblePositions.length) {
        return visiblePositions[0].position;
    }
    else {
        // first priority position from passed array
        return positions[0];
    }
}
/**
 * Calculate element position in relation with handle
 * @param handle HTMLElement Target element
 * @param element Placement Element to be positioned
 * @param position Placement position
 * @param offsets Placement offsets
 * @returns OffsetOptions
 */
function getPlacementCoordinates(handle, element, position, offsets, hasText) {
    var _a, _b;
    if (hasText === void 0) { hasText = false; }
    var elementWidth = element.offsetWidth, elementHeight = element.offsetHeight;
    var _c = getElementPositionRectangle(handle), targetTop = _c.top, targetLeft = _c.left, targetWidth = _c.width, targetHeight = _c.height;
    /*
    Template to convert smartplacement.topOffset/leftOffset to offsets per position:
    top: {
       topOffset: -topOffset,
       leftOffset: 0
    },
    'top-left': {
       topOffset: -topOffset,
       leftOffset: + leftOffset
    },
    'top-right': {
       topOffset: -topOffset,
       leftOffset: + leftOffset
    },
    bottom: {
       topOffset: +topOffset,
       leftOffset: 0
    },
    'bottom-left': {
       topOffset: +topOffset,
       leftOffset: +leftOffset
    },
    'bottom-right': {
       topOffset: +topOffset,
       leftOffset: +leftOffset
    },
    right: {
       topOffset: 0,
       leftOffset: +leftOffset
    },
    left: {
       topOffset: 0,
       leftOffset: -leftOffset
    }
     */
    var checkLines = multiLineTextPositionAndCoordinates(handle, element);
    if (hasText && checkLines.position) {
        return {
            top: checkLines.y,
            left: checkLines.x
        };
    }
    var topOffset = ((_a = offsets[position]) === null || _a === void 0 ? void 0 : _a.topOffset) || 0;
    var leftOffset = ((_b = offsets[position]) === null || _b === void 0 ? void 0 : _b.leftOffset) || 0;
    switch (position) {
        case 'top':
            return {
                top: targetTop - elementHeight + topOffset,
                left: targetLeft + targetWidth / 2 - elementWidth / 2 + leftOffset,
            };
        case 'top-left':
            return {
                top: targetTop - elementHeight + topOffset,
                left: targetLeft + targetWidth - elementWidth + leftOffset
            };
        case 'top-right':
            return {
                top: targetTop - elementHeight + topOffset,
                left: targetLeft + leftOffset
            };
        case 'bottom':
            return {
                top: targetTop + targetHeight + topOffset,
                left: targetLeft + targetWidth / 2 - elementWidth / 2 + leftOffset
            };
        case 'bottom-left':
            return {
                top: targetTop + targetHeight + topOffset,
                left: targetLeft + targetWidth - elementWidth + leftOffset
            };
        case 'bottom-right':
            return {
                top: targetTop + targetHeight + topOffset,
                left: targetLeft + leftOffset
            };
        case 'right':
            return {
                top: targetTop + targetHeight / 2 - elementHeight / 2 + topOffset,
                left: targetLeft + targetWidth + leftOffset
            };
        case 'left':
            return {
                top: targetTop + targetHeight / 2 - elementHeight / 2 + topOffset,
                left: targetLeft - elementWidth + leftOffset
            };
        default:
            throw new Error('Wrong placement position');
    }
}
/**
 * Function to check if the tooltip target text element takes more than 1 line.
 * If yes:
 * - we check the beginning of the text, if the text's beginning width + tooltip width more than window width,
 * we check the tail of the text (in case of true we return text's beginning coordinates as a tooltip place).
 * - And if the text's tail width + tooltip width is more than window width, we show tooltip in the bottom or top.
 */
function multiLineTextPositionAndCoordinates(target, element) {
    var elementStyles = getComputedStyle(element);
    var targetClientRects = Array.from(target.getClientRects());
    var tooltipWidth = parseFloat(elementStyles.width);
    var tooltipHeight = parseFloat(elementStyles.height);
    var pos = {
        position: '',
        x: 0,
        y: 0
    };
    var leftOffset = 24, topOffset = tooltipHeight / 2 - 10;
    if (targetClientRects.length > 1) {
        if (targetClientRects[0].width + tooltipWidth + leftOffset <= window.innerWidth) {
            return {
                position: 'left',
                x: targetClientRects[0].left - tooltipWidth - leftOffset,
                y: targetClientRects[0].top - topOffset
            };
        }
        else if (targetClientRects[targetClientRects.length - 1].width + tooltipWidth + leftOffset <= window.innerWidth) {
            return {
                position: 'right',
                x: targetClientRects[targetClientRects.length - 1].right + leftOffset,
                y: targetClientRects[targetClientRects.length - 1].top - topOffset
            };
        }
    }
    return pos;
}
